import { Dropdown, Menu } from 'antd';
import classnames from 'classnames';
import _ from 'lodash';
import { CheckboxInput, EcgToolbarArrowIconBlack as ToolbarArrowIcon } from '@/shared/ui';
import { GAIN_VALUES, HP_VALUES } from '@/shared/config';
import { sizeThumbnailMenu, thumbnailChannelList } from '@/entities/ecg';
import { checkHasChannel } from '@/shared/lib/thumbnailConfig';
import useBeatThumbnailConfig from './useBeatThumbnailConfig';

export type BeatThumbnailConfigProps = {
  isDisabled?: boolean;
  activeButton?: any;
};

/**
 * Configuration bar controlling how beat thumbnails are drawn, covering channels, size, gain and the high-pass filter. Reads the current values and their change handlers from useBeatThumbnailConfig and lays the four dropdowns out in a row, every one of them greyed out together while the isDisabled prop is set.
 *
 * @param props - Carries isDisabled, which greys out all four dropdowns.
 * @returns The thumbnail configuration bar holding the channel, size, gain and high-pass dropdowns.
 */
const BeatThumbnailConfig = ({ isDisabled, activeButton }: BeatThumbnailConfigProps) => {
  const {
    ecgDataMap,
    highPass,
    size,
    gain,
    channels,
    state,
    setState,
    onClickItemSize,
    onClickItemGain,
    onClickItemHighPass,
    onChangeChannels,
  } = useBeatThumbnailConfig({ activeButton });

  /**
   * Renders the thumbnail size picker for the beat thumbnail configuration. Turns each entry of sizeThumbnailMenu into a menu item, highlights the one equal to the current size and shows it on the toggle button.
   *
   * @returns The size dropdown with its value menu.
   */
  const renderSizeDropdown = () => {
    const items: any[] = [];
    _.forEach(sizeThumbnailMenu, (x, i) =>
      items.push({
        key: `size-${i}`,
        label: <div className={classnames(x === size ? 'selected' : '')}>{x}</div>,
        value: x,
      }),
    );
    return (
      <Dropdown
        disabled={isDisabled}
        trigger={['click']}
        overlay={<Menu onClick={onClickItemSize} className="thumbnail-size-menu" items={items} />}
      >
        <button id="beats-thumbnail-size-btn" className="thumbnail-size-text">
          {`Size: ${size}`}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  /**
   * Renders the gain picker for the beat thumbnail configuration. Turns each entry of GAIN_VALUES into a menu item labelled in mm/mV, highlights the one equal to the current gain, and shows the active value on the toggle button.
   *
   * @returns The gain dropdown with its value menu.
   */
  const renderGainDropdown = () => {
    const items: any[] = [];
    _.forEach(GAIN_VALUES, (x, i) =>
      items.push({
        key: `gain-${i}`,
        label: <div className={classnames(x === gain ? 'selected' : '')}>{`${x} mm/mV`}</div>,
        value: x,
      }),
    );
    return (
      <Dropdown
        disabled={isDisabled}
        trigger={['click']}
        overlay={<Menu onClick={onClickItemGain} className="thumbnail-size-menu" items={items} />}
      >
        <button id="beats-change-gain-btn" className="thumbnail-size-text">
          {`Gain: ${gain}`}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  /**
   * Renders the high-pass filter picker for the beat thumbnail configuration. Turns each entry of HP_VALUES into a menu item labelled in Hz while rendering zero as No, highlights the current selection and mirrors it on the toggle button.
   *
   * @returns The high-pass dropdown with its value menu.
   */
  const renderHighPassDropdown = () => {
    const items: any[] = [];
    _.forEach(HP_VALUES, (x, i) =>
      items.push({
        key: `highpass-${i}`,
        label: (
          <div className={classnames(x === highPass ? 'selected' : '')}>
            {x !== 0 ? `${x} Hz` : 'No'}
          </div>
        ),
        value: x,
      }),
    );
    return (
      <Dropdown
        disabled={isDisabled}
        trigger={['click']}
        overlay={
          <Menu onClick={onClickItemHighPass} className="thumbnail-size-menu" items={items} />
        }
      >
        <button id="beats-change-high-pass-btn" className="thumbnail-size-text">
          {`HP: ${highPass !== 0 ? highPass : 'No'}`}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  /**
   * Renders the ECG channel picker for the beat thumbnail configuration. Builds a checkbox item per entry of thumbnailChannelList, skipping the first three unless the loaded ECG data map actually carries that channel and labelling the last one Default, and shows the selection as a comma-joined list or None on the toggle button.
   *
   * @returns The channel dropdown with its checkbox menu.
   */
  const renderChannelDropdown = () => {
    const items: any[] = [];
    _.forEach(thumbnailChannelList, (channel, index) => {
      if (index < 3) {
        if (checkHasChannel(ecgDataMap.channels, channel)) {
          items.push({
            key: `channel-${index}`,
            label: (
              <CheckboxInput
                name="channel"
                data={{
                  label: `${channel.label}`,
                  value: channel.value,
                }}
                checked={_.includes(channels, channel.value)}
                onChange={onChangeChannels}
                id={_.uniqueId('checkbox-thumbnail-channel-')}
              />
            ),
            value: channel.value,
          });
        }
      } else {
        items.push({
          key: `channel-${index}`,
          label: (
            <CheckboxInput
              name="channel"
              data={{
                label: 'Default',
                value: channel.value,
              }}
              checked={_.includes(channels, channel.value)}
              onChange={onChangeChannels}
              id={_.uniqueId('checkbox-thumbnail-channel-')}
            />
          ),
          value: channel.value,
        });
      }
    });
    return (
      <Dropdown
        disabled={isDisabled}
        trigger={['click']}
        overlay={<Menu className="thumbnail-size-menu" items={items} />}
        open={state.visibleDropdownChannel}
        onOpenChange={(open) => setState({ visibleDropdownChannel: open })}
      >
        <button className="thumbnail-channel-text">
          {`CH: ${_.includes(channels, 4) ? 'Default' : channels.join(', ') || 'None'}`}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  return (
    <div className="thumbnail-config-container">
      <div className="beat-action-title">Thumbnail configuration</div>
      <div className="thumbnail-config">
        <div className="channel-config">{renderChannelDropdown()}</div>
        <div className="size-config">{renderSizeDropdown()}</div>
        <div className="gain-config">{renderGainDropdown()}</div>
        <div className="highpass-config">{renderHighPassDropdown()}</div>
      </div>
    </div>
  );
};

export default BeatThumbnailConfig;
